import { motion, Variants } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';

import { TColorKeys, IColor } from 'theme/palette';
import { ListItemMediaIcon } from 'common/components/ListItem/ListItemMediaIcon';
import { withSxProp } from 'common/utils/props';

const listItemMediaStyle: SxProps = {
    minHeight: { xs: '50px', sm: '106px' },
    minWidth: { xs: '70px', sm: '150px' },
    borderRadius: '4px',
    color: 'common.white',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 300ms',
    mr: { xs: 2, sm: 3 },
};

interface IProps {
    disableHover?: boolean;
    isDate?: boolean;
    isText?: boolean;
    image?: string;
    color?: TColorKeys;
    colorVariant?: keyof IColor;
    children?: React.ReactNode;
    contentEditable?: boolean;
    skeleton?: boolean;
    size?: 'small';
    sx?: SxProps;
}

export const ListItemMedia = ({
    disableHover,
    isDate,
    isText,
    image,
    color,
    colorVariant = 'main',
    children,
    skeleton,
    size,
    sx,
    ...other
}: IProps) => {
    const theme = useTheme();
    const themeColor = color ? theme.vars.palette[color] : null;
    const hoverVariant = colorVariant === 'main' ? 'accent' : 'main';

    if (skeleton) {
        return <Skeleton sx={sx} variant="rectangular" />;
    }

    // Enable color animation when themeColor is found and no image is given, or when isDate is true.
    const motionVariants: Variants =
        themeColor && (!image || isDate) && !disableHover
            ? {
                  initListItemButton: {
                      backgroundColor: themeColor[colorVariant],
                  },
                  hoverListItemButton: {
                      backgroundColor: themeColor[hoverVariant],
                      transition: { duration: 0.2 },
                  },
              }
            : {};

    return (
        <Box
            component={motion.div}
            sx={[
                listItemMediaStyle,
                {
                    ...(image && { backgroundImage: `url('${image}')` }),
                    ...(color && { bgcolor: `${color}.${colorVariant}` }),
                    ...(size === 'small' && {
                        minHeight: { sm: '92px' },
                        minWidth: { sm: '130px' },
                    }),
                },
                {
                    ...(isDate &&
                        !!image && {
                            backgroundPosition: 'right center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            p: 0,
                        }),
                },
                ...withSxProp(sx),
            ]}
            transition={{ duration: 0.2 }}
            variants={motionVariants}
            {...other}
        >
            {children && !isText && !isDate && (
                <ListItemMediaIcon $show={!image} size={size}>
                    {children}
                </ListItemMediaIcon>
            )}

            {(isText || isDate) && children}
        </Box>
    );
};
